import { useEffect, useRef, useState } from 'react';
import {
  audioSessionManager,
  TTSPlayer,
  TTSSource,
  MicRecorder,
  TranscriptionSource,
  VoiceTelemetry,
  VoiceConversationStateMachine,
} from '../services/audio/index.js';
import { IntakeRepository } from '../services/intake/index.js';
import useVoiceConversation from '../hooks/useVoiceConversation.js';

const TEST_VOICE_ID = 'ys3XeJJA4ArWMhRpcX1D';

const PAPER = '#F2EBDC';
const INK = '#1B1814';
const MUTED = '#6a5e44';
const BORDER = '#d8cdb1';

export default function AudioModulesTest() {
  const [logs, setLogs] = useState([]);
  const logRef = useRef([]);

  const [asmState, setAsmState] = useState(audioSessionManager.getState());
  const [ctxState, setCtxState] = useState('—');
  const [ctxTime, setCtxTime] = useState('—');

  const [playerState, setPlayerState] = useState('idle');
  const [playerTtfa, setPlayerTtfa] = useState(null);
  const [playerTotal, setPlayerTotal] = useState(null);

  const [sourceChunks, setSourceChunks] = useState(0);
  const [sourceBytes, setSourceBytes] = useState(0);
  const [sourceFirstChunkMs, setSourceFirstChunkMs] = useState(null);

  const [pipelineTtfa1, setPipelineTtfa1] = useState(null);
  const [pipelineTtfa2, setPipelineTtfa2] = useState(null);

  const playerRef = useRef(null);
  const sourceRef = useRef(null);

  if (!playerRef.current) playerRef.current = new TTSPlayer(audioSessionManager);
  if (!sourceRef.current) sourceRef.current = new TTSSource();

  // ── Section 5 state (state machine) ─────────────────────────────
  const stateMachineRef = useRef(null);
  const [smState, setSmState] = useState('idle');
  const [smTranscript, setSmTranscript] = useState(null);
  const [smError, setSmError] = useState(null);
  const [smHistory, setSmHistory] = useState([]);

  function ensureStateMachine() {
    if (stateMachineRef.current) return stateMachineRef.current;
    const sm = new VoiceConversationStateMachine({
      audioSessionManager,
      ttsPlayer: new TTSPlayer(audioSessionManager),
      ttsSource: new TTSSource(),
      micRecorder: new MicRecorder(audioSessionManager),
      transcriptionSource: new TranscriptionSource(),
      telemetry: new VoiceTelemetry(),
    });
    sm.on('stateChange', ({ from, to, reason }) => {
      const t = (performance.now() / 1000).toFixed(3);
      setSmState(to);
      setSmHistory((prev) =>
        [...prev, `[${t}s] ${from} → ${to} (${reason || '—'})`].slice(-50)
      );
      addLog(`SM ${from} → ${to} (${reason || '—'})`);
    });
    sm.on('speakAudible', ({ ttfaMs }) =>
      addLog(`SM speakAudible @ ${ttfaMs.toFixed(0)} ms`)
    );
    sm.on('listenEnd', ({ durationMs }) =>
      addLog(`SM listenEnd dur=${durationMs} ms`)
    );
    sm.on('transcribeEnd', ({ text }) => {
      setSmTranscript(text);
      addLog(`SM transcribeEnd: "${text}"`);
    });
    sm.on('turnComplete', ({ transcript }) =>
      addLog(`SM turnComplete: "${transcript}"`)
    );
    sm.on('error', ({ error, context }) => {
      setSmError(`${context}: ${error?.message}`);
      addLog(`SM error in ${context}: ${error?.message}`);
    });
    stateMachineRef.current = sm;
    return sm;
  }

  async function smStart() {
    setSmError(null);
    try {
      const sm = ensureStateMachine();
      if (sm.getState() === 'idle') await sm.init();
      await sm.speak(
        'Hello, this is a test of the conversation state machine. Please say something in response.',
        TEST_VOICE_ID
      );
    } catch (e) {
      addLog(`SM start failed: ${e.message}`);
    }
  }

  async function smListen() {
    setSmError(null);
    try {
      const sm = ensureStateMachine();
      await sm.listen(15000);
    } catch (e) {
      addLog(`SM listen failed: ${e.message}`);
    }
  }

  async function smStopListen() {
    try {
      const sm = ensureStateMachine();
      await sm.stopListening();
    } catch (e) {
      addLog(`SM stopListening failed: ${e.message}`);
    }
  }

  async function smSpeakBack() {
    setSmError(null);
    try {
      const sm = ensureStateMachine();
      if (!smTranscript) return;
      await sm.speak(`I heard you say: ${smTranscript}`, TEST_VOICE_ID);
    } catch (e) {
      addLog(`SM speak-back failed: ${e.message}`);
    }
  }

  function smCancel() {
    if (stateMachineRef.current) stateMachineRef.current.cancel();
  }

  function smReset() {
    try {
      setSmError(null);
      if (stateMachineRef.current) stateMachineRef.current.reset();
    } catch (e) {
      addLog(`SM reset failed: ${e.message}`);
    }
  }

  // ── Section 6 state (intake persistence) ────────────────────────
  const repoRef = useRef(null);
  const wiredRef = useRef(false);
  const [repoSessionId, setRepoSessionId] = useState(null);
  const [repoMessageCount, setRepoMessageCount] = useState(0);
  const [repoPendingCount, setRepoPendingCount] = useState(0);
  const [repoLastSync, setRepoLastSync] = useState('—');
  const [repoMessages, setRepoMessages] = useState([]);

  function ensureRepo() {
    if (repoRef.current) return repoRef.current;
    const repo = new IntakeRepository({ telemetry: new VoiceTelemetry() });
    repo.on('turnPersisted', ({ record }) => {
      addLog(
        `Repo persisted ${record.role} turn #${record.turn_number} (${record.id.slice(0, 8)})`
      );
      refreshRepoCounts();
    });
    repo.on('turnSynced', ({ messageId }) => {
      setRepoLastSync(`synced ${messageId.slice(0, 8)} @ ${new Date().toLocaleTimeString()}`);
      addLog(`Repo synced ${messageId.slice(0, 8)}`);
      refreshRepoCounts();
    });
    repo.on('syncFailure', ({ messageId, error, retryCount }) => {
      setRepoLastSync(
        `failure ${messageId.slice(0, 8)} (retry ${retryCount}): ${error?.message?.slice(0, 80)}`
      );
      addLog(
        `Repo sync failure ${messageId.slice(0, 8)} retry=${retryCount}: ${error?.message}`
      );
      refreshRepoCounts();
    });
    repo.on('syncGiveUp', ({ messageId, lastError }) => {
      setRepoLastSync(
        `gave up ${messageId.slice(0, 8)}: ${lastError?.message?.slice(0, 80)}`
      );
      addLog(`Repo gave up on ${messageId.slice(0, 8)}: ${lastError?.message}`);
      refreshRepoCounts();
    });
    repoRef.current = repo;
    return repo;
  }

  async function refreshRepoCounts() {
    const repo = repoRef.current;
    if (!repo || !repoSessionId) return;
    try {
      const messages = await repo.getMessages(repoSessionId);
      setRepoMessageCount(messages.length);
      setRepoPendingCount(
        messages.filter(
          (m) => m.sync_status === 'pending' || m.sync_status === 'failed'
        ).length
      );
    } catch {}
  }

  async function repoStartSession() {
    try {
      const repo = ensureRepo();
      await repo.init();
      const sid = repo.startSession();
      setRepoSessionId(sid);
      setRepoMessageCount(0);
      setRepoPendingCount(0);
      setRepoMessages([]);
      addLog(`Repo session started: ${sid}`);
    } catch (e) {
      addLog(`Repo start failed: ${e.message}`);
    }
  }

  async function repoSaveGuideTurn() {
    try {
      const repo = ensureRepo();
      if (!repoSessionId) return addLog('Start a session first');
      await repo.saveTurn({
        session_id: repoSessionId,
        role: 'guide',
        content: 'Test guide message ' + Date.now(),
      });
    } catch (e) {
      addLog(`Repo save guide failed: ${e.message}`);
    }
  }

  async function repoSaveUserTurn() {
    try {
      const repo = ensureRepo();
      if (!repoSessionId) return addLog('Start a session first');
      await repo.saveTurn({
        session_id: repoSessionId,
        role: 'user',
        content: 'Test user message ' + Date.now(),
      });
    } catch (e) {
      addLog(`Repo save user failed: ${e.message}`);
    }
  }

  function repoWireStateMachine() {
    if (wiredRef.current) return addLog('Already wired to state machine');
    const repo = ensureRepo();
    const sm = ensureStateMachine();
    if (!repoSessionId) return addLog('Start a session first');

    sm.on('turnComplete', ({ transcript }) => {
      repo
        .saveTurn({
          session_id: repoSessionId,
          role: 'user',
          content: transcript,
        })
        .catch((e) => addLog(`Repo save (user) failed: ${e.message}`));
    });

    const originalSpeak = sm.speak.bind(sm);
    sm.speak = async (text, voiceId) => {
      try {
        await repo.saveTurn({
          session_id: repoSessionId,
          role: 'guide',
          content: text,
        });
      } catch (e) {
        addLog(`Repo save (guide pre-speak) failed: ${e.message}`);
      }
      return originalSpeak(text, voiceId);
    };

    wiredRef.current = true;
    addLog('Wired repo ↔ state machine');
  }

  async function repoShowMessages() {
    try {
      const repo = ensureRepo();
      if (!repoSessionId) return addLog('Start a session first');
      const messages = await repo.getMessages(repoSessionId);
      setRepoMessages(messages);
      addLog(`Repo loaded ${messages.length} messages from IDB`);
    } catch (e) {
      addLog(`Repo getMessages failed: ${e.message}`);
    }
  }

  async function repoForceSync() {
    try {
      const repo = ensureRepo();
      const queue = repo.getRetryQueue();
      await queue.processNow();
      addLog('Repo force-sync tick complete');
      refreshRepoCounts();
    } catch (e) {
      addLog(`Repo force sync failed: ${e.message}`);
    }
  }

  // ── Section 7 state (useVoiceConversation hook) ─────────────────
  const [jwtInput, setJwtInput] = useState('');

  const {
    state,
    currentTranscript,
    lastError,
    messages,
    speak,
    startListening,
    stopListening,
    reset,
    init,
  } = useVoiceConversation({
    userId: 'test-user-section7',
    authToken: jwtInput.trim() || null,
    sessionId: null,
    // voiceId omitted — hook uses its default ('ys3XeJJA4ArWMhRpcX1D').
    onTurnComplete: (text) =>
      addLog(`[Section7] turnComplete: "${text}"`),
    onError: (err) =>
      addLog(`[Section7] error: ${err?.message || err}`),
  });

  async function hookInit() {
    try {
      await init();
      addLog('[Section7] init complete');
    } catch (e) {
      addLog(`[Section7] init failed: ${e.message}`);
    }
  }

  async function hookSpeakGreeting() {
    try {
      await speak("Hello, I'm your guide. How can I help you today?");
    } catch (e) {
      addLog(`[Section7] speak failed: ${e.message}`);
    }
  }

  async function hookListen() {
    try {
      await startListening();
    } catch (e) {
      addLog(`[Section7] listen failed: ${e.message}`);
    }
  }

  async function hookStop() {
    try {
      await stopListening();
    } catch (e) {
      addLog(`[Section7] stop failed: ${e.message}`);
    }
  }

  function hookReset() {
    try {
      reset();
    } catch (e) {
      addLog(`[Section7] reset failed: ${e.message}`);
    }
  }

  const truncate = (s, n) =>
    s && s.length > n ? s.slice(0, n) + '…' : s || '';

  function addLog(line) {
    const t = (performance.now() / 1000).toFixed(3);
    const entry = `[${t}s] ${line}`;
    console.log('[AudioModulesTest]', entry);
    logRef.current = [...logRef.current, entry].slice(-200);
    setLogs(logRef.current);
  }

  useEffect(() => {
    if (typeof window !== 'undefined' && !window.eruda) {
      const s = document.createElement('script');
      s.src = '//cdn.jsdelivr.net/npm/eruda';
      s.onload = () => window.eruda && window.eruda.init();
      document.body.appendChild(s);
    }
  }, []);

  useEffect(() => {
    const onState = (next) => {
      setAsmState(next);
      addLog(`ASM statechange → ${next}`);
    };
    const onInterrupted = () => addLog('ASM interrupted (unexpected suspension)');
    audioSessionManager.on('statechange', onState);
    audioSessionManager.on('interrupted', onInterrupted);
    return () => {
      audioSessionManager.off('statechange', onState);
      audioSessionManager.off('interrupted', onInterrupted);
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      try {
        const c = audioSessionManager.getContext();
        setCtxState(c.state);
        setCtxTime(c.currentTime.toFixed(3));
      } catch {
        setCtxState('—');
        setCtxTime('—');
      }
    }, 250);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const player = playerRef.current;
    const onStateChange = (next) => {
      setPlayerState(next);
      addLog(`Player stateChange → ${next}`);
    };
    const onAudible = ({ ttfaMs }) => {
      setPlayerTtfa(ttfaMs);
      addLog(`Player audible @ ${ttfaMs.toFixed(0)} ms`);
    };
    const onEnd = ({ totalMs, audioDurationMs }) => {
      setPlayerTotal(totalMs);
      addLog(
        `Player end: total=${totalMs.toFixed(0)} ms, audio=${audioDurationMs.toFixed(0)} ms`
      );
    };
    const onError = ({ error }) => addLog(`Player error: ${error?.message}`);
    const onCancel = () => addLog('Player cancelled');
    player.on('stateChange', onStateChange);
    player.on('audible', onAudible);
    player.on('end', onEnd);
    player.on('error', onError);
    player.on('cancel', onCancel);
    return () => {
      player.off('stateChange', onStateChange);
      player.off('audible', onAudible);
      player.off('end', onEnd);
      player.off('error', onError);
      player.off('cancel', onCancel);
    };
  }, []);

  // ── Section 1 handlers ───────────────────────────────────────────
  async function handleUnlock() {
    try {
      addLog('ASM unlock → start');
      await audioSessionManager.unlock();
      addLog('ASM unlock → done');
    } catch (e) {
      addLog(`ASM unlock failed: ${e.message}`);
    }
  }
  async function handleSuspend() {
    try {
      await audioSessionManager.suspend();
      addLog('ASM suspend → done');
    } catch (e) {
      addLog(`ASM suspend failed: ${e.message}`);
    }
  }
  async function handleResume() {
    try {
      await audioSessionManager.resume();
      addLog('ASM resume → done');
    } catch (e) {
      addLog(`ASM resume failed: ${e.message}`);
    }
  }

  // ── Section 2 handlers ───────────────────────────────────────────
  async function handlePlayTest() {
    try {
      setPlayerTtfa(null);
      setPlayerTotal(null);
      if (audioSessionManager.getState() === 'locked') {
        await audioSessionManager.unlock();
      }
      addLog('Player: fetching /api/speak-stream');
      const res = await fetch('/api/speak-stream', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: 'Audio module test playback',
          voiceId: TEST_VOICE_ID,
        }),
      });
      if (!res.ok || !res.body) {
        addLog(`Player: fetch failed status=${res.status}`);
        return;
      }
      addLog('Player: stream open, handing to TTSPlayer.play()');
      await playerRef.current.play(res.body);
    } catch (e) {
      addLog(`Player.play threw: ${e.message}`);
    }
  }
  function handleCancel() {
    playerRef.current.cancel();
  }

  // ── Section 3 handlers ───────────────────────────────────────────
  async function handleStreamOnly() {
    try {
      setSourceChunks(0);
      setSourceBytes(0);
      setSourceFirstChunkMs(null);
      const t0 = performance.now();
      addLog('Source: calling stream()');
      const stream = await sourceRef.current.stream(
        'Audio module test stream',
        TEST_VOICE_ID
      );
      const reader = stream.getReader();
      let chunks = 0;
      let bytes = 0;
      let firstAt = null;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        if (firstAt == null) {
          firstAt = performance.now() - t0;
          setSourceFirstChunkMs(firstAt);
          addLog(`Source: first chunk @ ${firstAt.toFixed(0)} ms`);
        }
        chunks += 1;
        bytes += value.byteLength;
        setSourceChunks(chunks);
        setSourceBytes(bytes);
      }
      addLog(`Source: stream end, ${chunks} chunks, ${bytes} bytes`);
    } catch (e) {
      addLog(`Source.stream failed: ${e.message}`);
    }
  }

  // ── Section 4 handlers ───────────────────────────────────────────
  async function runPipelineOnce(label) {
    const t0 = performance.now();
    if (audioSessionManager.getState() === 'locked') {
      await audioSessionManager.unlock();
    }
    addLog(`${label}: unlocked, opening stream`);
    const stream = await sourceRef.current.stream(
      'Audio module integration test',
      TEST_VOICE_ID
    );

    const player = playerRef.current;
    let audibleMs = null;
    const onAudible = ({ ttfaMs }) => {
      audibleMs = performance.now() - t0;
      addLog(
        `${label}: audible @ end-to-end ${audibleMs.toFixed(0)} ms (player TTFA ${ttfaMs.toFixed(0)} ms)`
      );
    };
    player.on('audible', onAudible);
    try {
      await player.play(stream);
    } finally {
      player.off('audible', onAudible);
    }
    return audibleMs;
  }

  async function handlePipelineSingle() {
    try {
      setPipelineTtfa1(null);
      setPipelineTtfa2(null);
      const ttfa = await runPipelineOnce('Pipeline#1');
      setPipelineTtfa1(ttfa);
    } catch (e) {
      addLog(`Pipeline failed: ${e.message}`);
    }
  }

  async function handlePipelineTwice() {
    try {
      setPipelineTtfa1(null);
      setPipelineTtfa2(null);
      const a = await runPipelineOnce('Pipeline#1');
      setPipelineTtfa1(a);
      addLog('Pipeline: waiting 1s before second run');
      await new Promise((r) => setTimeout(r, 1000));
      const b = await runPipelineOnce('Pipeline#2');
      setPipelineTtfa2(b);
    } catch (e) {
      addLog(`Pipeline twice failed: ${e.message}`);
    }
  }

  async function handleRecordAndSpeak() {
    let stream;
    try {
      if (audioSessionManager.getState() === 'locked') {
        await audioSessionManager.unlock();
      }
      addLog('Record+Speak: getUserMedia()');
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      rec.start();
      addLog('Record+Speak: recording 2s');
      await new Promise((r) => setTimeout(r, 2000));
      rec.stop();
      await new Promise((resolve) => (rec.onstop = resolve));
      stream.getTracks().forEach((t) => t.stop());
      addLog('Record+Speak: mic stopped, opening TTS pipeline');
      const t0 = performance.now();
      const ttsStream = await sourceRef.current.stream(
        'Echo received',
        TEST_VOICE_ID
      );
      const player = playerRef.current;
      const onAudible = () =>
        addLog(
          `Record+Speak: audible @ ${(performance.now() - t0).toFixed(0)} ms`
        );
      player.on('audible', onAudible);
      try {
        await player.play(ttsStream);
      } finally {
        player.off('audible', onAudible);
      }
    } catch (e) {
      addLog(`Record+Speak failed: ${e.message}`);
      if (stream) {
        try {
          stream.getTracks().forEach((t) => t.stop());
        } catch {}
      }
    }
  }

  return (
    <div
      style={{
        fontFamily:
          '-apple-system, BlinkMacSystemFont, system-ui, sans-serif',
        background: PAPER,
        color: INK,
        minHeight: '100vh',
        padding: 16,
        paddingBottom: 80,
      }}
    >
      <h1 style={{ fontSize: 18, margin: '0 0 12px' }}>
        Audio modules test
      </h1>
      <p style={{ fontSize: 12, color: MUTED, marginBottom: 18 }}>
        Standalone — no Clerk, no AppContext. Visit on iPhone for the
        real validation.
      </p>

      <Section title="1. AudioSessionManager">
        <ButtonRow>
          <Btn onClick={handleUnlock}>Unlock</Btn>
          <Btn onClick={handleSuspend}>Suspend</Btn>
          <Btn onClick={handleResume}>Resume</Btn>
        </ButtonRow>
        <Status>
          <Metric label="state" value={asmState} />
          <Metric label="AudioContext.state" value={ctxState} />
          <Metric label="currentTime" value={ctxTime} />
        </Status>
      </Section>

      <Section title="2. TTSPlayer">
        <ButtonRow>
          <Btn onClick={handlePlayTest}>Play test audio</Btn>
          <Btn onClick={handleCancel}>Cancel</Btn>
        </ButtonRow>
        <Status>
          <Metric label="state" value={playerState} />
          <Metric
            label="TTFA"
            value={playerTtfa == null ? '—' : `${playerTtfa.toFixed(0)} ms`}
          />
          <Metric
            label="total"
            value={
              playerTotal == null ? '—' : `${playerTotal.toFixed(0)} ms`
            }
          />
        </Status>
      </Section>

      <Section title="3. TTSSource (no playback)">
        <ButtonRow>
          <Btn onClick={handleStreamOnly}>Stream from API</Btn>
        </ButtonRow>
        <Status>
          <Metric label="chunks" value={sourceChunks} />
          <Metric label="bytes" value={sourceBytes} />
          <Metric
            label="first chunk"
            value={
              sourceFirstChunkMs == null
                ? '—'
                : `${sourceFirstChunkMs.toFixed(0)} ms`
            }
          />
        </Status>
      </Section>

      <Section title="4. Integration">
        <ButtonRow>
          <Btn onClick={handlePipelineSingle}>Full pipeline (single)</Btn>
          <Btn onClick={handlePipelineTwice}>Full pipeline (twice)</Btn>
          <Btn onClick={handleRecordAndSpeak}>Record + Speak</Btn>
        </ButtonRow>
        <Status>
          <Metric
            label="TTFA #1"
            value={
              pipelineTtfa1 == null ? '—' : `${pipelineTtfa1.toFixed(0)} ms`
            }
          />
          <Metric
            label="TTFA #2"
            value={
              pipelineTtfa2 == null ? '—' : `${pipelineTtfa2.toFixed(0)} ms`
            }
          />
        </Status>
      </Section>

      <Section title="5. Conversation state machine">
        <ButtonRow>
          <SmBtn onClick={smStart}>Start conversation</SmBtn>
          <SmBtn onClick={smListen} disabled={smState !== 'unlocked'}>
            Listen for response
          </SmBtn>
          <SmBtn onClick={smStopListen} disabled={smState !== 'listening'}>
            Stop listening
          </SmBtn>
          <SmBtn
            onClick={smSpeakBack}
            disabled={smState !== 'unlocked' || !smTranscript}
          >
            Speak back the transcript
          </SmBtn>
          <SmBtn onClick={smCancel}>Cancel</SmBtn>
          <SmBtn onClick={smReset}>Reset</SmBtn>
        </ButtonRow>
        <Status>
          <Metric label="state" value={smState} />
          <Metric
            label="last transcript"
            value={smTranscript ? `"${smTranscript}"` : '—'}
          />
          <Metric label="last error" value={smError || '—'} />
        </Status>
        <div
          style={{
            background: '#fff',
            border: `1px solid ${BORDER}`,
            borderRadius: 12,
            padding: 12,
            marginTop: 10,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize: 12,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            maxHeight: 200,
            overflowY: 'auto',
          }}
        >
          {smHistory.length === 0
            ? '(no transitions yet)'
            : smHistory.join('\n')}
        </div>
      </Section>

      <Section title="6. Intake persistence">
        <p style={{ fontSize: 12, color: MUTED, margin: '0 0 10px' }}>
          Local-first writes will succeed. Supabase sync will fail with
          auth errors until Day 4 integrates this into the
          authenticated Intake screen — that's expected on this test
          page.
        </p>
        <ButtonRow>
          <SmBtn onClick={repoStartSession}>Start session</SmBtn>
          <SmBtn onClick={repoSaveGuideTurn} disabled={!repoSessionId}>
            Save sample guide turn
          </SmBtn>
          <SmBtn onClick={repoSaveUserTurn} disabled={!repoSessionId}>
            Save sample user turn
          </SmBtn>
          <SmBtn
            onClick={repoWireStateMachine}
            disabled={!repoSessionId}
          >
            Wire to state machine
          </SmBtn>
          <SmBtn onClick={repoShowMessages} disabled={!repoSessionId}>
            Show stored messages
          </SmBtn>
          <SmBtn onClick={repoForceSync}>Force sync</SmBtn>
        </ButtonRow>
        <Status>
          <Metric
            label="session_id"
            value={repoSessionId ? repoSessionId : '—'}
          />
          <Metric label="messages (IDB)" value={repoMessageCount} />
          <Metric label="pending sync" value={repoPendingCount} />
          <Metric label="last sync" value={repoLastSync} />
        </Status>
        {repoMessages.length > 0 && (
          <div
            style={{
              background: '#fff',
              border: `1px solid ${BORDER}`,
              borderRadius: 12,
              padding: 12,
              marginTop: 10,
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: 12,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              maxHeight: 240,
              overflowY: 'auto',
            }}
          >
            {repoMessages
              .map(
                (m) =>
                  `#${m.turn_number} [${m.role}] (${m.sync_status}) ${m.content}`
              )
              .join('\n')}
          </div>
        )}
      </Section>

      <Section title="7. Full Conversation (useVoiceConversation hook)">
        <p style={{ fontSize: 12, color: MUTED, margin: '0 0 10px' }}>
          Paste a Clerk JWT below to validate cloud sync to Supabase
          intake_messages. Get the JWT by signing in to the app in
          another tab, then opening devtools console and running:{' '}
          <code>
            window.Clerk.session.getToken().then(t =&gt; console.log(t))
          </code>
          . Copy the printed string. Leave empty to test IDB-only
          persistence.
        </p>
        <textarea
          rows={3}
          placeholder="Paste Clerk JWT here (eyJ...) or leave empty"
          value={jwtInput}
          onChange={(e) => setJwtInput(e.target.value)}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize: 12,
            border: `1px solid ${BORDER}`,
            borderRadius: 12,
            padding: 10,
            marginBottom: 10,
            resize: 'vertical',
          }}
        />
        <ButtonRow>
          <SmBtn onClick={hookInit} disabled={state !== 'idle'}>
            Initialize
          </SmBtn>
          <SmBtn
            onClick={hookSpeakGreeting}
            disabled={state !== 'unlocked'}
          >
            Speak greeting
          </SmBtn>
          <SmBtn onClick={hookListen} disabled={state !== 'unlocked'}>
            Listen
          </SmBtn>
          <SmBtn onClick={hookStop} disabled={state !== 'listening'}>
            Stop
          </SmBtn>
          <SmBtn onClick={hookReset}>Reset</SmBtn>
        </ButtonRow>
        <Status>
          <Metric label="state" value={state} />
          <Metric
            label="currentTranscript"
            value={currentTranscript ? `"${currentTranscript}"` : '—'}
          />
          <Metric
            label="lastError"
            value={lastError?.message || 'none'}
          />
          <Metric label="messages.length" value={messages.length} />
        </Status>
        <div
          style={{
            background: '#fff',
            border: `1px solid ${BORDER}`,
            borderRadius: 12,
            padding: 0,
            marginTop: 10,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize: 12,
            maxHeight: 240,
            overflowY: 'auto',
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '40px 60px 1fr 80px',
              gap: 8,
              padding: '8px 12px',
              borderBottom: `1px solid ${BORDER}`,
              color: MUTED,
              fontWeight: 600,
            }}
          >
            <span>#</span>
            <span>role</span>
            <span>content</span>
            <span>status</span>
          </div>
          {messages.length === 0 ? (
            <div style={{ padding: '8px 12px', color: MUTED }}>
              (no messages yet)
            </div>
          ) : (
            messages.map((m, i) => (
              <div
                key={m.id || `${m.turn_number}-${i}`}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '40px 60px 1fr 80px',
                  gap: 8,
                  padding: '6px 12px',
                  borderBottom: `1px solid ${BORDER}`,
                  wordBreak: 'break-word',
                }}
              >
                <span>{m.turn_number}</span>
                <span>{m.role}</span>
                <span>{truncate(m.content, 60)}</span>
                <span>{m.sync_status || 'pending'}</span>
              </div>
            ))
          )}
        </div>
      </Section>

      <Section title="Event log">
        <div
          style={{
            background: '#fff',
            border: `1px solid ${BORDER}`,
            borderRadius: 12,
            padding: 12,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize: 12,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            maxHeight: 320,
            overflowY: 'auto',
          }}
        >
          {logs.length === 0 ? '(no events yet)' : logs.join('\n')}
        </div>
      </Section>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section style={{ marginBottom: 22 }}>
      <h2 style={{ fontSize: 14, margin: '0 0 8px' }}>{title}</h2>
      {children}
    </section>
  );
}

function ButtonRow({ children }) {
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
        marginBottom: 10,
      }}
    >
      {children}
    </div>
  );
}

function Btn({ onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        appearance: 'none',
        background: INK,
        color: PAPER,
        border: 'none',
        borderRadius: 12,
        padding: '12px 14px',
        fontSize: 14,
        fontWeight: 600,
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}

function Status({ children }) {
  return (
    <div
      style={{
        background: '#fff',
        border: `1px solid ${BORDER}`,
        borderRadius: 12,
        padding: 12,
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        fontSize: 12,
      }}
    >
      {children}
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 8,
        padding: '2px 0',
      }}
    >
      <span style={{ color: MUTED }}>{label}</span>
      <span>{String(value)}</span>
    </div>
  );
}

function SmBtn({ onClick, disabled, children }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        appearance: 'none',
        background: INK,
        color: PAPER,
        border: 'none',
        borderRadius: 12,
        padding: '12px 14px',
        fontSize: 14,
        fontWeight: 600,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.4 : 1,
      }}
    >
      {children}
    </button>
  );
}
